import React from 'react';
import GridNum from './GridNum';
import HelpTip from './HelpTip';
import { MONTHS } from '../lib/brokerages';
import { fmtUSD } from '../lib/math';

export default function PipelineGrid({ deals, dispatch }) {
  const totalGCI = deals.reduce(
    (s, d) => s + (Number(d.sale_price) || 0) * (Number(d.commission_pct) || 0) / 100,
    0,
  );
  const avgPrice = deals.length
    ? deals.reduce((s, d) => s + Number(d.sale_price || 0), 0) / deals.length
    : 0;
  const avgComm = deals.length
    ? deals.reduce((s, d) => s + Number(d.commission_pct || 0), 0) / deals.length
    : 0;

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
      data-testid="bta-pipeline-grid"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">
                <span className="inline-flex items-center">Nickname<HelpTip text="A label for this deal so you can keep them straight. Doesn't affect any math." /></span>
              </th>
              <th className="px-3 py-2 text-left">
                <span className="inline-flex items-center">Side<HelpTip text="Which side of the deal you're on. Buyer-side, listing-side, or dual agent (representing both sides, where legal). Doesn't change the math by itself — the commission % you enter already reflects your side." /></span>
              </th>
              <th className="px-3 py-2 text-right">
                <span className="inline-flex items-center">Sale price<HelpTip text="The expected final sale price of this property." /></span>
              </th>
              <th className="px-3 py-2 text-right">
                <span className="inline-flex items-center">Comm %<HelpTip text="Your commission percentage on this deal. If the total commission is 5% split evenly between both sides, enter 2.5%. For listings you keep both sides, enter the full number (e.g., 5%)." /></span>
              </th>
              <th className="px-3 py-2 text-right">
                <span className="inline-flex items-center">Referral %<HelpTip text="Percentage of this deal's commission you owe to a referring agent or relocation company. Comes off the top before any brokerage or team split. Leave at 0 if no referral was paid." /></span>
              </th>
              <th className="px-3 py-2 text-right">
                <span className="inline-flex items-center">Concessions<HelpTip text="Dollars you agreed to pay out of your commission toward the client's costs — buyer credits, seller concessions, closing gifts. Reduces your commission before any splits." /></span>
              </th>
              <th className="px-3 py-2 text-left">
                <span className="inline-flex items-center">Close month<HelpTip text="The expected close month for this deal. Used so the tool can figure out which month you'd hit your cap — deals are processed in close-month order." /></span>
              </th>
              <th className="px-3 py-2 text-right">
                <span className="inline-flex items-center">TC / flat fees<HelpTip text="Any flat per-deal fees you pay — transaction coordinator, compliance, attorney. $300-$500 per deal is typical for a TC." /></span>
              </th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {deals.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className="px-2 py-1">
                  <input
                    className="w-32 px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#2FA163] rounded text-sm"
                    value={d.nickname}
                    onChange={(e) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { nickname: e.target.value } })}
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    className="w-24 px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#2FA163] rounded text-sm bg-transparent"
                    value={d.side}
                    onChange={(e) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { side: e.target.value } })}
                  >
                    <option value="buyer">Buyer</option>
                    <option value="listing">Listing</option>
                    <option value="dual">Dual</option>
                  </select>
                </td>
                <td className="px-2 py-1 text-right">
                  <GridNum className="w-28" value={d.sale_price} onChange={(v) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { sale_price: v } })} />
                </td>
                <td className="px-2 py-1 text-right">
                  <GridNum className="w-20" value={d.commission_pct} onChange={(v) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { commission_pct: v } })} />
                </td>
                <td className="px-2 py-1 text-right">
                  <GridNum className="w-20" value={d.referral_out_pct} onChange={(v) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { referral_out_pct: v } })} />
                </td>
                <td className="px-2 py-1 text-right">
                  <GridNum className="w-24" value={d.concessions} onChange={(v) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { concessions: v } })} />
                </td>
                <td className="px-2 py-1">
                  <select
                    className="px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#2FA163] rounded text-sm bg-transparent"
                    value={d.close_month}
                    onChange={(e) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { close_month: Number(e.target.value) } })}
                  >
                    {MONTHS.map((m, idx) => (
                      <option key={idx} value={idx}>{m}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1 text-right">
                  <GridNum className="w-20" value={d.tc_fees} onChange={(v) => dispatch({ type: 'UPDATE_DEAL', id: d.id, patch: { tc_fees: v } })} />
                </td>
                <td className="px-2 py-1">
                  <button
                    type="button"
                    onClick={() => dispatch({ type: 'REMOVE_DEAL', id: d.id })}
                    data-testid="bta-deal-remove-btn"
                    className="text-slate-400 hover:text-red-500 text-xs"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan="9" className="p-2">
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'ADD_DEAL' })}
                  data-testid="bta-deal-add-btn"
                  className="text-sm text-[#286C4E] hover:text-[#092825] underline"
                >
                  + Add deal
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-slate-700">
            <tr>
              <td className="px-3 py-2" colSpan="2">{deals.length} deals</td>
              <td className="px-3 py-2 text-right">avg {fmtUSD(avgPrice)}</td>
              <td className="px-3 py-2 text-right">avg {avgComm.toFixed(1)}%</td>
              <td className="px-3 py-2" colSpan="4"></td>
              <td className="px-3 py-2 text-right" data-testid="bta-pipeline-total-gci">{fmtUSD(totalGCI)} GCI</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
